import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import VoiceRecorder from '../components/VoiceRecorder'

const VoiceRecord: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  const teamId = searchParams.get('team')
  const userId = searchParams.get('user')
  
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (!teamId || !userId) {
      setError('Missing team or user information. Please try again from Slack.')
    }
  }, [teamId, userId])

  const handleStandupSubmitted = (data: any) => {
    console.log('Voice standup submitted:', data)
    setSubmitted(true)
    
    // Auto-close after a few seconds or show success message
    setTimeout(() => {
      window.close() // Try to close the window if opened as popup
    }, 3000)
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="max-w-md mx-auto text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error</div>
          <p className="mb-4 text-muted-foreground">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="max-w-md mx-auto text-center">
          <div className="text-green-600 text-xl mb-4">✅ Success!</div>
          <p className="mb-4 text-muted-foreground">
            Your voice standup has been processed and saved successfully!
          </p>
          <p className="text-sm text-muted-foreground">
            This window will close automatically, or you can close it manually.
          </p>
          <button 
            onClick={() => window.close()}
            className="mt-4 rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
          >
            Close Window
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-background shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <h1 className="text-2xl font-semibold text-foreground">🎤 Voice Standup Recording</h1>
            <p className="text-muted-foreground">Record your daily standup and we'll transcribe it for you</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {teamId && userId && (
          <VoiceRecorder
            onStandupSubmitted={handleStandupSubmitted}
            userId={userId}
            teamId={teamId}
            supabaseUrl={import.meta.env.VITE_SUPABASE_URL as string}
          />
        )}
        
        {/* Instructions */}
        <div className="mt-8 border border-primary/30 bg-primary/5 p-6">
          <h3 className="mb-4 text-base font-semibold text-foreground">How to record your standup:</h3>
          <div className="space-y-3 text-foreground">
            <div className="flex items-start">
              <span className="font-bold mr-2">1.</span>
              <span>Click "Start Recording" and speak naturally about your standup</span>
            </div>
            <div className="flex items-start">
              <span className="font-bold mr-2">2.</span>
              <span>Mention what you did yesterday, what you plan to do today, and any blockers</span>
            </div>
            <div className="flex items-start">
              <span className="font-bold mr-2">3.</span>
              <span>You can pause and resume recording if needed</span>
            </div>
            <div className="flex items-start">
              <span className="font-bold mr-2">4.</span>
              <span>Review your recording by playing it back</span>
            </div>
            <div className="flex items-start">
              <span className="font-bold mr-2">5.</span>
              <span>Click "Submit Standup" to process and save your standup</span>
            </div>
          </div>
          
          <div className="mt-4 border border-primary/20 bg-primary/10 p-3">
            <p className="text-sm text-foreground">
              <strong>💡 Pro tip:</strong> Speak clearly and naturally. Our AI is designed to understand 
              conversational speech and will automatically extract the key information from your recording.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VoiceRecord
